import type { FC } from 'react';
import React, { useEffect, useState, useCallback, useContext } from 'react';
import { graphql, useFragment } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { PopupProps } from '@atlaskit/popup/types';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-atlaskit-theme
import { layers } from '@atlaskit/theme/constants';

import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import {
	ExperienceStart,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getMark } from '@confluence/performance/entry-points/performance.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';
import { SSR_PARTIAL_COMPONENT } from '@confluence/ssr-utilities/entry-points/ssrPartialSuccess.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { expVal } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import { useSSR, SSREvent } from '../../hooks/index.ts';
import { CONTENT_ANALYTICS_VIEWERS_METRIC } from '../../perf.config.ts';
import { AnalyticsDialogSsr } from '../AnalyticsDialog/AnalyticsDialogSsr.tsx';
import { isExpectedError } from '../analyticsUtils.ts';

import { SSR_EVENTS_CAPTURE_KEY } from './constants.ts';
import { useAnalyticsByLineEntryPoint } from './useAnalyticsByLineEntryPoint.tsx';
import type {
	AnalyticsByLine_query$data,
	AnalyticsByLine_query$key,
} from './__generated__/AnalyticsByLine_query.graphql.ts';

export type AnalyticsByLineProps = {
	contentId: string;
	flags: FlagsStateContainer;
	contentAuthorId?: string;
	analyticsBylineFragmentRef?: AnalyticsByLine_query$key | null;
};

type AnalyticsByLineInternalProps = AnalyticsByLineProps & {
	analyticsBylineFragmentData?: AnalyticsByLine_query$data | null;
};

const AnalyticsBylineFragment = graphql`
	fragment AnalyticsByLine_query on Query @argumentDefinitions(contentId: { type: "ID!" }) {
		contentAnalyticsViewers(contentId: $contentId) {
			count
		}
		user: confluence_atlassianUser(current: true) {
			id
			confluence {
				permissionType
			}
		}
	}
`;

const AnalyticsByLineInternal: FC<AnalyticsByLineInternalProps> = ({
	contentId,
	contentAuthorId,
	analyticsBylineFragmentData,
}) => {
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	const renderServerPlaceholder = useIsSSROrHydrating();
	const { onSSRCapturedEvent, wasComponentSSRed, wasSSREventTriggered } = useSSR({
		ssrEventCaptureKey: SSR_EVENTS_CAPTURE_KEY,
		name: SSR_PARTIAL_COMPONENT.AnalyticsByLine,
	});
	const [isPopupOpen, setPopupOpen] = useState(wasSSREventTriggered(SSREvent.CLICK));

	const {
		error,
		loading,

		popupContent,
		popupContentExperience,
		onPopupContentClose,

		popupTrigger,
		popupTriggerExperience,
		onPopupTriggerSSREvent,
		hideAnalyticsByLine,
	} = useAnalyticsByLineEntryPoint({
		contentId,
		wasComponentSSRed,
		contentAuthorId,
		analyticsBylineFragmentData,
	});

	useEffect(() => {
		if (wasComponentSSRed) {
			// https://hello.atlassian.net/l/cp/942Xd6LH#Instrumenting-your-feature
			CONTENT_ANALYTICS_VIEWERS_METRIC.markFMP(getMark('CFP-63.ssr-ttr'));
		}
	}, [wasComponentSSRed]);

	const experienceTracker = useContext(ExperienceTrackerContext);

	useEffect(() => {
		if (!wasComponentSSRed) {
			setPopupOpen(false);
		}
	}, [contentId, wasComponentSSRed]);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'displayed',
				actionSubject: 'analytics',
				actionSubjectId: contentId,
				objectId: contentId,
				source: 'confluence-frontend',
			},
		}).fire();
	}, [contentId, createAnalyticsEvent]);

	const onPopupClose: PopupProps['onClose'] = (...args) => {
		onPopupContentClose?.(...args);
		setPopupOpen(false);
	};

	const onPopupTriggerClick = useCallback(() => {
		onPopupTriggerSSREvent?.(SSREvent.CLICK);
		setPopupOpen(true);
		experienceTracker.start({ name: popupContentExperience });
	}, [experienceTracker, onPopupTriggerSSREvent, popupContentExperience]);
	useEffect(() => {
		onSSRCapturedEvent(SSREvent.CLICK, onPopupTriggerClick);
	}, [onSSRCapturedEvent, onPopupTriggerClick]);

	const onPopupTriggerFocus = useCallback(() => {
		onPopupTriggerSSREvent?.(SSREvent.FOCUS);
	}, [onPopupTriggerSSREvent]);
	useEffect(() => {
		onSSRCapturedEvent(SSREvent.FOCUS, onPopupTriggerFocus);
	}, [onSSRCapturedEvent, onPopupTriggerFocus]);

	const onPopupTriggerHover = useCallback(() => {
		onPopupTriggerSSREvent?.(SSREvent.HOVER);
	}, [onPopupTriggerSSREvent]);
	useEffect(() => {
		onSSRCapturedEvent(SSREvent.HOVER, onPopupTriggerHover);
	}, [onSSRCapturedEvent, onPopupTriggerHover]);

	useEffect(() => {
		if (error) {
			if (popupTriggerExperience) {
				experienceTracker.abort({
					name: popupTriggerExperience,
					reason: error.name,
				});
			}
			if (isExpectedError(error)) {
				markErrorAsHandled(error);
			}
		}
	}, [error, experienceTracker, popupTriggerExperience]);

	useEffect(() => {
		if (!loading && !error && popupTriggerExperience) {
			experienceTracker.succeed({ name: popupTriggerExperience });
		}
	}, [error, experienceTracker, loading, popupTriggerExperience]);

	const zIndex = layers.dialog();

	const { Popup } = useChoreographerAssets();

	return !hideAnalyticsByLine ? (
		<>
			{popupTriggerExperience ? <ExperienceStart name={popupTriggerExperience} /> : null}
			<Popup
				testId="analytics-by-line-popup"
				isOpen={isPopupOpen}
				onClose={onPopupClose}
				placement="bottom-start"
				trigger={(props) => {
					const { 'aria-expanded': _, ...triggerProps } = props;
					const trigger = popupTrigger({
						...props,
						onClick: onPopupTriggerClick,
					});

					return (
						<span
							{...triggerProps}
							onFocus={onPopupTriggerFocus}
							onMouseOver={onPopupTriggerHover}
							data-vc="analytics-byline-wrapper"
							{...ssrPlaceholderIdProp}
						>
							<PageSegmentLoadStart
								metric={CONTENT_ANALYTICS_VIEWERS_METRIC}
								// If we did SSR we want the start time to be the page start time.
								// If we did not SSR we want the start time to be now.
								isCustomStart={!wasComponentSSRed}
							/>
							{error && <ErrorDisplay error={error} />}
							{trigger && (
								<>
									{trigger}
									<PageSegmentLoadEnd metric={CONTENT_ANALYTICS_VIEWERS_METRIC} />
								</>
							)}
						</span>
					);
				}}
				content={(props) => popupContent({ ...props, zIndex })}
				zIndex={zIndex}
				messageId="confluence-analytics-analytics-by-line-popup"
				messageType="transactional"
			/>
			{renderServerPlaceholder && <AnalyticsDialogSsr zIndex={zIndex} />}
		</>
	) : null;
};

const AnalyticsByLineFragmentReader: FC<
	AnalyticsByLineProps & { analyticsBylineFragmentRef: AnalyticsByLine_query$key }
> = ({ analyticsBylineFragmentRef, ...props }) => {
	const analyticsBylineFragmentData = useFragment(
		AnalyticsBylineFragment,
		analyticsBylineFragmentRef,
	);

	return (
		<AnalyticsByLineInternal {...props} analyticsBylineFragmentData={analyticsBylineFragmentData} />
	);
};

const AnalyticsByLineFragmentBoundary: FC<AnalyticsByLineProps> = (props) => {
	if (
		props.analyticsBylineFragmentRef &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_analytics_byline_frag_reader') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false)
	) {
		return (
			<AnalyticsByLineFragmentReader
				{...props}
				analyticsBylineFragmentRef={props.analyticsBylineFragmentRef}
			/>
		);
	}

	return <AnalyticsByLineInternal {...props} />;
};

export const AnalyticsByLine: FC<Omit<AnalyticsByLineProps, 'flags'>> = withErrorBoundary({
	attribution: Attribution.CC_ANALYTICS,
})(withFlags(AnalyticsByLineFragmentBoundary));
